import { useState, useEffect, useRef } from "react";
import { customInstance } from "@/lib/axios-instance";
import { API_CONFIG } from "@/constants/api";
import { RecipientsResponse, TransMasterData } from "../types";
import { toast } from "react-hot-toast";
import { getErrorMessage, isAxiosError } from "@/lib/error-utils";

export const useWebchatData = (transId: string | null, agentType?: string | null) => {
  const [recipients, setRecipients] = useState<RecipientsResponse | null>(null);
  const [transMasterData, setTransMasterData] = useState<TransMasterData | null>(null);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [isLoadingTransMaster, setIsLoadingTransMaster] = useState(false);
  
  // Track if data has been fetched to prevent duplicate calls
  const hasFetchedRecipients = useRef(false);
  const hasFetchedTransMaster = useRef(false);
  const lastTransId = useRef<string | null>(null);
  const lastAgentType = useRef<string | null>(null);

  // Fetch recipients data when transId is available
  useEffect(() => {
    const fetchRecipients = async () => {
      if (!transId) return;
      
      // Check if we've already fetched for this transId and agentType
      if (hasFetchedRecipients.current && 
          lastTransId.current === transId && 
          lastAgentType.current === agentType) {
        return;
      }

      setIsLoadingRecipients(true);
      hasFetchedRecipients.current = true;
      lastTransId.current = transId;
      lastAgentType.current = agentType ?? null;
      
      try {
        // Build the URL with agentType parameter if provided
        const baseUrl = `${API_CONFIG.ENDPOINTS.TRANS_MASTER_RECIPIENTS}/transId/${transId}`;
        const url = agentType ? `${baseUrl}?agentType=${encodeURIComponent(agentType)}` : baseUrl;
        
        const response = await customInstance<RecipientsResponse>({
          url,
          method: 'GET'
        });
        setRecipients(response.data);
      } catch (error) {
        console.error("Error fetching recipients:", getErrorMessage(error));
        toast.error(`Failed to load recipients: ${getErrorMessage(error)}`);
        // Reset flag on error to allow retry
        hasFetchedRecipients.current = false;
      } finally {
        setIsLoadingRecipients(false);
      }
    };

    fetchRecipients();
  }, [transId, agentType]);

  // Fetch trans-master data when transId is available
  useEffect(() => {
    const fetchTransMasterData = async () => {
      if (!transId) return;
      
      // Check if we've already fetched for this transId
      if (hasFetchedTransMaster.current && lastTransId.current === transId) {
        return;
      }

      setIsLoadingTransMaster(true);
      hasFetchedTransMaster.current = true;
      
      try {
        const response = await customInstance<TransMasterData>({
          url: `${API_CONFIG.ENDPOINTS.TRANS_MASTER}/${transId}`,
          method: 'GET'
        });
        setTransMasterData(response.data);
      } catch (error) {
        console.error("Error fetching trans-master data:", getErrorMessage(error));
        // Don't show error toast for this as it's not critical for chat functionality
        // But log the specific error for debugging
        if (isAxiosError(error) && error.response?.data) {
          console.warn("Trans-master fetch error:", error.response.data);
        }
        // Reset flag on error to allow retry
        hasFetchedTransMaster.current = false;
      } finally {
        setIsLoadingTransMaster(false);
      }
    };

    fetchTransMasterData();
  }, [transId]);

  return {
    recipients,
    transMasterData,
    isLoadingRecipients,
    isLoadingTransMaster
  };
};